import React from "react";
import t from "../../../../i18n";
import createIOSortType from "../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useSortedData from "../../../../lib/hooks/useSortedData";
import type { SortableValue } from "diagnostics-lib/comparator";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Ellipsis from "@ui/Ellipsis";
import { useCanUserDo } from "../../../canUserDo";
import { useModals } from "@ui/lib/hooks/useModal";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import { css } from "@linaria/core";
import classNames from "classnames";
import { useLocalExternalLink, type LocalExternalLink } from "../../../../api/useExternalLinks";
import Link from "@ui/Link";
import { color } from "@ui";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, PencilLine } from "@ui/IconV2/react";
import Modal from "@ui/Modal";
import { ModalFormDeleteLink } from "./ModalFormDeleteLink";
import { ModalFormUpsertLink } from "./ModalFormUpsertLink";

const columns = ["frName", "enName", "url"] as const;
type Column = (typeof columns)[number];
const useSortTranslation = createGlobalState({
  storage: "session",
  key: "externalLinkSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "frName",
    direction: "asc",
  },
  scoped: false,
});
interface P extends React.HtmlHTMLAttributes<HTMLDivElement> {
  className?: string;
}

const sortSelector: { [key in Column]: (b: LocalExternalLink) => SortableValue | null } = {
  frName: b => b.frName,
  enName: b => b.enName,
  url: b => b.url,
};
const computeRowKey = (translation: LocalExternalLink) =>
  translation.frName + " " + translation.enName + " " + translation.url;

const ApplicationLinkTable = ({ className }: P) => {
  const externalLinks = useLocalExternalLink();

  const [sort, setSort] = useSortTranslation({});
  const canEdit = useCanUserDo("ADMIN_SETTING_MANAGE");

  const { modal, closeModal, openModal } = useModals<"edit" | "delete">();

  const [selectedExternalLink, setSelectedExternalLink] = React.useState<LocalExternalLink | null>(
    null,
  );

  const handleOpenModal = React.useCallback(
    (action: "edit" | "delete", externalLink: LocalExternalLink) => {
      openModal(action);
      setSelectedExternalLink(externalLink);
    },
    [openModal],
  );

  const handleCloseModal = React.useCallback(() => {
    closeModal();
    setSelectedExternalLink(null);
  }, [closeModal]);

  const externalLinkMetadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<LocalExternalLink> } = {
      frName: {
        label: t("app.administration.application.link.modal.form.text_fr.label"),
        render: (externalLink: LocalExternalLink) => (
          <div className={frLinkName}>
            <Ellipsis maxNumberOfLines={2}>{externalLink.frName}</Ellipsis>
            {canEdit && (
              <div className={actionIcons}>
                <ButtonIconV2
                  title={t("commons.message_card.edit")}
                  onClick={() => handleOpenModal("edit", externalLink)}
                >
                  <PencilLine size="m" />
                </ButtonIconV2>
                <ButtonIconV2
                  title={t("commons.message_card.delete")}
                  onClick={() => handleOpenModal("delete", externalLink)}
                >
                  <DeleteBinLine size="m" />
                </ButtonIconV2>
                {selectedExternalLink && (
                  <>
                    <Modal
                      isOpen={modal === "edit"}
                      title={t("app.administration.application.link.modal.title.edit")}
                      onRequestClose={handleCloseModal}
                    >
                      <ModalFormUpsertLink
                        externalLink={selectedExternalLink}
                        onRequestClose={handleCloseModal}
                      />
                    </Modal>
                    <Modal
                      isOpen={modal === "delete"}
                      title={t("app.administration.application.link.modal.title.delete")}
                      onRequestClose={handleCloseModal}
                    >
                      <ModalFormDeleteLink
                        externalLinkId={selectedExternalLink.id}
                        externalLinkName={selectedExternalLink.localName}
                        onRequestClose={handleCloseModal}
                      />
                    </Modal>
                  </>
                )}
              </div>
            )}
          </div>
        ),
        minWidth: "200px",
        maxWidth: "400px",
        sortable: true,
        sticky: true,
        showEmptyMark: true,
        alignment: "left",
      },
      enName: {
        label: t("app.administration.application.link.modal.form.text_en.label"),
        render: (externalLink: LocalExternalLink) => (
          <Ellipsis maxNumberOfLines={2}>{externalLink.enName}</Ellipsis>
        ),
        minWidth: "250px",
        maxWidth: "400px",
        sortable: false,
        showEmptyMark: true,
      },
      url: {
        label: "URL",
        render: (externalLink: LocalExternalLink) => (
          <Ellipsis maxNumberOfLines={2} title={externalLink.url}>
            <Link className={styledLink} href={externalLink.url} target="_blank">
              {externalLink.url}
            </Link>
          </Ellipsis>
        ),
        minWidth: "250px",
        maxWidth: "400px",
        sortable: false,
        showEmptyMark: true,
      },
    };
    return _;
  }, [canEdit, modal, selectedExternalLink, handleOpenModal, handleCloseModal]);

  const sortedDatas = useSortedData(externalLinks, sortSelector[sort.column], sort.direction);

  if (externalLinks.length === 0) return null;

  return (
    <>
      <TableCard
        title={null}
        headerLeft={null}
        headerRight={null}
        className={classNames(className, tableCard)}
      >
        <Table
          stickyHeader
          datas={sortedDatas}
          metadata={externalLinkMetadata}
          sort={sort}
          onSort={setSort}
          computeRowKey={computeRowKey}
          isRowInteractive={() => true}
        />
      </TableCard>
    </>
  );
};

export default ApplicationLinkTable;

//#region CSS
const styledLink = css`
  color: ${color.lightNitrogen500_Main};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
`;
const frLinkName = css`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;
const actionIcons = css`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
    align-items: center;
  }
`;
const tableCard = css`
  max-width: 1200px;
  height: auto;
`;
//#endregion CSS
